/**
 * Contrato canônico para persistência de veículos vindos de integrações.
 * Mantém normalização, limites e montagem do payload fora de providers específicos.
 *
 * RELEASE: 2026-08-23 v1.2.0 - Aceita resolução manual (status `manual`): marca/modelo/ano
 *          escolhidos pelo usuário ou categoria própria do tenant (tipo OUTROS), garantindo
 *          que nenhum veículo entre sem vínculo relacional válido.
 * RELEASE: 2026-08-23 v1.1.0 - Comparação campo a campo (diff) entre o que já está
 *          cadastrado no sistema e o que o provider devolveu, para atualização parcial.
 * RELEASE: 2026-08-23 v1.0.0 - Versão inicial para o cadastro automático atômico.
 */


import type { LookupVehicle } from '@/hooks/integrations/useCustomerDocumentLookup';
import type { VehicleEnrichment } from '@/hooks/vehicles/useVehicleFipeEnrichment';

/** Limpa texto comum sem permitir conteúdo HTML vindo do provider. */
function cleanText(value: string | null | undefined, maxLength: number): string | null {
  const sanitized = (value ?? '')
    .replace(/<[^>]*>/g, ' ')
    .replace(/[\u0000-\u001F\u007F]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, maxLength);
  return sanitized || null;
}

/** Normaliza identificadores veiculares antes da validação definitiva no banco. */
function cleanIdentifier(
  value: string | null | undefined,
  maxLength: number,
  digitsOnly = false,
): string | null {
  const normalized = digitsOnly
    ? (value ?? '').replace(/\D/g, '')
    : (value ?? '').replace(/[^A-Za-z0-9]/g, '').toUpperCase();
  return normalized.slice(0, maxLength) || null;
}

/** Payload serializável aceito pela RPC transacional de importação. */
export interface CanonicalImportedVehicle {
  vehicle_type: LookupVehicle['vehicle_type'];
  category_id: string | null;
  brand_id: string | null;
  brand_name: string | null;
  model_id: string | null;
  model_name: string | null;
  year_code: string | null;
  year: number | null;
  fuel: string | null;
  color: string | null;
  plate: string | null;
  renavam: string | null;
  chassis: string | null;
  fipe_code: string | null;
  fipe_value: number | null;
  notes: string | null;
  fipe_resolved: boolean;
}

/**
 * Converte o retorno multi-provider para o mesmo conjunto relacional usado no
 * formulário manual. Veículos FIPE só são marcados como resolvidos com IDs válidos.
 */
export function buildCanonicalImportedVehicle(
  vehicle: LookupVehicle,
  enrichment: VehicleEnrichment | undefined,
): CanonicalImportedVehicle {
  // v1.2.0: manual entra no mesmo contrato dos matches automáticos
  const isFipeResolved =
    enrichment?.status === 'confirmed' ||
    enrichment?.status === 'suggested' ||
    enrichment?.status === 'manual';

  // Tipo pode ter sido corrigido manualmente (ex.: provider mandou algo fora da FIPE)
  const vehicleType = enrichment?.vehicle_type ?? vehicle.vehicle_type;
  const categoryId = vehicleType === 'OTHER' ? enrichment?.category_id ?? null : null;

  const notes = [
    vehicle.coverage_number ? `Cobertura ${vehicle.coverage_number}` : null,
    vehicle.contract_type,
    vehicle.tracker_number ? `Rastreador ${vehicle.tracker_number}` : null,
  ]
    .filter(Boolean)
    .join(' • ');

  return {
    vehicle_type: vehicleType,
    category_id: categoryId,
    brand_id: isFipeResolved ? enrichment.brand_id ?? null : null,
    brand_name: cleanText(enrichment?.brand_name ?? vehicle.brand_name, 120),
    model_id: isFipeResolved ? enrichment.model_id ?? null : null,
    model_name: cleanText(enrichment?.model_name ?? vehicle.model_name, 180),
    year_code: isFipeResolved ? cleanText(enrichment.year_code, 30) : null,
    year: enrichment?.year ?? vehicle.year,
    fuel: cleanText(enrichment?.fuel ?? vehicle.fuel, 60),
    color: cleanText(vehicle.color, 60),
    plate: cleanIdentifier(vehicle.plate, 8),
    renavam: cleanIdentifier(vehicle.renavam, 11, true),
    chassis: cleanIdentifier(vehicle.chassis, 17),
    fipe_code: cleanText(enrichment?.fipe_code ?? vehicle.fipe_code, 30),
    fipe_value: enrichment?.fipe_value ?? vehicle.fipe_value,
    notes: cleanText(notes, 2_000),
    // Resolvido = tem vínculo relacional válido (FIPE marca+modelo ou categoria do tenant)
    fipe_resolved:
      vehicleType === 'OTHER'
        ? Boolean(categoryId)
        : Boolean(isFipeResolved && enrichment?.brand_id && enrichment?.model_id),
  };
}
// =============================================================================
// COMPARAÇÃO COM O CADASTRO EXISTENTE (atualização parcial)
// =============================================================================

/** Campos do veículo que podem ser atualizados a partir da integração. */
export type UpdatableVehicleField =
  | 'brand'
  | 'model'
  | 'year'
  | 'fuel'
  | 'color'
  | 'renavam'
  | 'chassis'
  | 'fipe_code'
  | 'fipe_value'
  | 'notes';

/** Valores atuais do veículo já cadastrado (devolvidos pela RPC de duplicidade). */
export interface ExistingVehicleSnapshot {
  vehicle_type?: string | null;
  brand_name?: string | null;
  model_name?: string | null;
  year?: number | null;
  fuel?: string | null;
  color?: string | null;
  plate?: string | null;
  renavam?: string | null;
  chassis?: string | null;
  fipe_code?: string | null;
  fipe_value?: number | null;
  notes?: string | null;
}

/** Linha de comparação exibida no wizard. */
export interface VehicleFieldDiff {
  field: UpdatableVehicleField;
  label: string;
  currentValue: string | null;
  incomingValue: string | null;
  /** Provider não trouxe dado: nunca pode sobrescrever o que já existe. */
  incomingIsEmpty: boolean;
  /** Campo vazio no sistema (preencher lacuna é seguro). */
  currentIsEmpty: boolean;
}

const FIELD_LABELS: Record<UpdatableVehicleField, string> = {
  brand: 'Marca',
  model: 'Modelo',
  year: 'Ano',
  fuel: 'Combustível',
  color: 'Cor',
  renavam: 'RENAVAM',
  chassis: 'Chassi',
  fipe_code: 'Código FIPE',
  fipe_value: 'Valor FIPE',
  notes: 'Observações',
};

/** Normaliza para comparação: sem acento, caixa e pontuação. */
function comparableValue(value: unknown): string {
  return String(value ?? '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-zA-Z0-9]/g, '')
    .toUpperCase();
}

/** Texto exibido na comparação (mantém o valor original legível). */
function displayValue(value: unknown): string | null {
  if (value === null || value === undefined) return null;
  const text = String(value).trim();
  return text.length > 0 ? text : null;
}

/**
 * Compara o veículo já cadastrado com o payload canônico da integração e
 * devolve apenas os campos divergentes. Campos iguais são omitidos.
 */
export function buildVehicleFieldDiffs(
  current: ExistingVehicleSnapshot,
  incoming: CanonicalImportedVehicle,
): VehicleFieldDiff[] {
  const pairs: Array<{ field: UpdatableVehicleField; current: unknown; incoming: unknown }> = [
    { field: 'brand', current: current.brand_name, incoming: incoming.brand_name },
    { field: 'model', current: current.model_name, incoming: incoming.model_name },
    { field: 'year', current: current.year, incoming: incoming.year },
    { field: 'fuel', current: current.fuel, incoming: incoming.fuel },
    { field: 'color', current: current.color, incoming: incoming.color },
    { field: 'renavam', current: current.renavam, incoming: incoming.renavam },
    { field: 'chassis', current: current.chassis, incoming: incoming.chassis },
    { field: 'fipe_code', current: current.fipe_code, incoming: incoming.fipe_code },
    { field: 'fipe_value', current: current.fipe_value, incoming: incoming.fipe_value },
    { field: 'notes', current: current.notes, incoming: incoming.notes },
  ];

  return pairs
    .filter((pair) => comparableValue(pair.current) !== comparableValue(pair.incoming))
    .map((pair) => ({
      field: pair.field,
      label: FIELD_LABELS[pair.field],
      currentValue: displayValue(pair.current),
      incomingValue: displayValue(pair.incoming),
      incomingIsEmpty: comparableValue(pair.incoming).length === 0,
      currentIsEmpty: comparableValue(pair.current).length === 0,
    }));
}
